/**
 * Version History drawer (Wave C #3).
 *
 * On the shared Drawer primitive, resizable (SA-814). List view: every bundle version, newest first, with a
 * derived change summary (diff vs its predecessor, client-side — Q5 label-free).
 * Selecting a row shows the block-level diff vs current + Restore (Task 9).
 */
import { useMemo, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/Drawer'
import {
  Modal, ModalContent, ModalHeader, ModalTitle, ModalBody, ModalFooter,
} from '@/components/ui/Modal'
import { useDrawerWidth } from '@/hooks/useDrawerWidth'
import { usePersonaBundleHistory } from '@/hooks/usePersonaBundleHistory'
import { ApiError } from '@/services/apiClient'
import { formatRelativeTime } from '@/utils/relativeTime'
import { blockFieldLabel } from './blockTaxonomy'
import { importOriginLine } from './importOriginText'
import { diffBundles, isEmptyDiff, summarizeChange } from './versionDiff'
import type { PersonaBundleResponse } from '@/types/personaWizard'

const COLLAPSED_ROWS = 30

interface VersionHistoryDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  personaId: string
  /** Bundle-draft dirt only (dirtyPaths.size > 0) — basics edits survive a restore. */
  dirty: boolean
  onRestore: (version: PersonaBundleResponse) => Promise<void>
}

export function VersionHistoryDrawer({
  open,
  onOpenChange,
  personaId,
  dirty,
  onRestore,
}: VersionHistoryDrawerProps) {
  const history = usePersonaBundleHistory(personaId, { enabled: open })
  const resize = useDrawerWidth('persona-version-history', { default: 560, min: 480 }, 'Resize version history panel')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [restoreError, setRestoreError] = useState<
    | { kind: 'validation'; errors: { capability_id: string; reason: string }[] }
    | { kind: 'generic' }
    | null
  >(null)

  const rows = useMemo(() => {
    const list = history.data ?? []
    return list.map((bundle, i) => ({
      bundle,
      summary: summarizeChange(list[i + 1], bundle),
      originLine: importOriginLine(bundle.import_origin, bundle.created_by_name),
    }))
  }, [history.data])

  const list = history.data ?? []
  const current = list.find((b) => b.is_current) ?? null
  const selected = selectedId ? list.find((b) => b.id === selectedId) ?? null : null
  const diff = useMemo(
    () => (selected && current ? diffBundles(current, selected) : null),
    [current, selected],
  )

  const handleOpenChange = (next: boolean) => {
    if (restoring) return
    if (!next) {
      setSelectedId(null)
      setShowAll(false)
      setConfirmOpen(false)
      setRestoreError(null)
    }
    onOpenChange(next)
  }

  const handleConfirmRestore = async () => {
    if (!selected) return
    setConfirmOpen(false)
    setRestoring(true)
    setRestoreError(null)
    try {
      await onRestore(selected)
      setSelectedId(null) // back to the list — the new version appears on top
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 422 &&
        typeof err.detail === 'object' &&
        err.detail !== null &&
        Array.isArray((err.detail as { errors?: unknown }).errors)
      ) {
        setRestoreError({
          kind: 'validation',
          errors: (err.detail as { errors: { capability_id: string; reason: string }[] }).errors,
        })
      } else {
        setRestoreError({ kind: 'generic' })
      }
    } finally {
      setRestoring(false)
    }
  }

  const visible = showAll ? rows : rows.slice(0, COLLAPSED_ROWS)

  return (
    <>
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent resize={resize}>
        <DrawerHeader className="items-start">
          <div>
            <DrawerTitle>Version history</DrawerTitle>
            <p className="text-xs text-muted-foreground">
              Every save is a version — restore any time.
            </p>
          </div>
        </DrawerHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {history.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {history.isError && (
              <p className="text-sm text-destructive">Couldn't load the version history.</p>
            )}
            {selectedId === null ? (
              <ol className="space-y-1">
                {visible.map(({ bundle, summary, originLine }) => (
                  <li key={bundle.id}>
                    <button
                      type="button"
                      aria-label={`View changes in v${bundle.version}`}
                      onClick={() => setSelectedId(bundle.id)}
                      className="w-full rounded-lg border border-transparent px-2 py-2 text-left hover:border-border hover:bg-muted/50"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <span className="tabular-nums">v{bundle.version}</span>
                        <span
                          className="text-xs font-normal text-muted-foreground"
                          title={bundle.created_at ?? undefined}
                        >
                          {bundle.created_at
                            ? formatRelativeTime(new Date(bundle.created_at).getTime())
                            : ''}
                          {' · '}
                          {bundle.created_by_name ?? '—'}
                        </span>
                        {bundle.is_current && (
                          <span className="ml-auto rounded-full bg-success/10 px-2 py-0.5 text-xs text-success">
                            Active
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
                        {summary}
                      </span>
                      {originLine && (
                        <span className="mt-0.5 block text-xs text-muted-foreground">{originLine}</span>
                      )}
                    </button>
                  </li>
                ))}
                {!showAll && rows.length > COLLAPSED_ROWS && (
                  <li>
                    <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
                      Show all {rows.length} versions
                    </Button>
                  </li>
                )}
              </ol>
            ) : selected && current && diff ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Back to versions"
                    onClick={() => { setSelectedId(null); setRestoreError(null) }}
                  >
                    <ChevronLeft className="h-4 w-4" /> Back
                  </Button>
                  <h3 className="text-sm font-semibold">v{selected.version} vs current</h3>
                </div>
                {dirty && (
                  <p className="text-xs text-muted-foreground">
                    Comparing saved versions — your unsaved edits aren't included.
                  </p>
                )}
                {isEmptyDiff(diff) && (
                  <p className="text-sm text-muted-foreground">
                    This version matches the current content.
                  </p>
                )}
                {diff.blocks.reorderedOnly && (
                  <p className="text-sm text-muted-foreground">
                    Same blocks, different order — restoring rearranges them without changing content.
                  </p>
                )}
                {diff.blocks.changed.map((c) => (
                  <div key={`chg-${c.category}-${c.ordinal}`} className="rounded-lg border border-border p-2">
                    <p className="mb-1 text-xs font-semibold">{blockFieldLabel(c.category, c.ordinal)}</p>
                    <pre className="whitespace-pre-wrap rounded bg-destructive/10 p-2 text-xs">{c.before}</pre>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-success/10 p-2 text-xs">{c.after}</pre>
                  </div>
                ))}
                {diff.blocks.added.map((c) => (
                  <div key={`add-${c.category}-${c.ordinal}`} className="rounded-lg border border-border p-2">
                    <p className="mb-1 text-xs font-semibold">
                      {blockFieldLabel(c.category, c.ordinal)} <span className="text-success">· restored</span>
                    </p>
                    <pre className="whitespace-pre-wrap rounded bg-success/10 p-2 text-xs">{c.after}</pre>
                  </div>
                ))}
                {diff.blocks.removed.map((c) => (
                  <div key={`rem-${c.category}-${c.ordinal}`} className="rounded-lg border border-border p-2">
                    <p className="mb-1 text-xs font-semibold">
                      {blockFieldLabel(c.category, c.ordinal)} <span className="text-destructive">· removed</span>
                    </p>
                    <pre className="whitespace-pre-wrap rounded bg-destructive/10 p-2 text-xs">{c.before}</pre>
                  </div>
                ))}
                {(diff.skills.added.length > 0 || diff.skills.removed.length > 0) && (
                  <p className="text-xs text-muted-foreground">
                    {diff.skills.added.map((s) => `+ ${s}`).join('  ')}{' '}
                    {diff.skills.removed.map((s) => `− ${s}`).join('  ')}
                  </p>
                )}
                {diff.otherChanged.map((label) => (
                  <p key={label} className="text-xs text-muted-foreground">
                    {label.charAt(0).toUpperCase() + label.slice(1)} differ.
                  </p>
                ))}
                {restoreError && (
                  <Alert variant="destructive" className="text-xs">
                    {restoreError.kind === 'generic' ? (
                      'Restore failed — the live persona was not changed.'
                    ) : (
                      <div>
                        <p className="font-semibold">
                          Restore blocked — this version needs credentials that aren't configured:
                        </p>
                        <ul className="mt-1 list-disc pl-4">
                          {restoreError.errors.map((e) => (
                            <li key={e.capability_id}>
                              {e.capability_id}: {e.reason}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-1">Fix the credentials or restore a different version.</p>
                      </div>
                    )}
                  </Alert>
                )}
              </div>
            ) : null}
          </div>

          {selected && (
            <div className="border-t border-border px-4 py-3">
              {selected.is_current ? (
                <p className="text-xs text-muted-foreground">This is the live version.</p>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  isLoading={restoring}
                  disabled={restoring}
                  onClick={() => setConfirmOpen(true)}
                >
                  {restoring ? 'Restoring…' : 'Restore this version'}
                </Button>
              )}
            </div>
          )}
      </DrawerContent>
    </Drawer>

      <Modal open={confirmOpen} onOpenChange={setConfirmOpen}>
        <ModalContent size="sm" showClose={false}>
          <ModalHeader>
            <ModalTitle>Restore v{selected?.version}?</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-muted-foreground">
              Its content becomes the new active version — your current setup stays in history.
            </p>
            {dirty && (
              <p className="mt-2 text-sm text-warning-strong">
                Your unsaved changes will be discarded.
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="default" onClick={() => void handleConfirmRestore()}>
              Restore
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
